/**
 * ImpStateTracker.tsx
 * ────────────────────────────────────────────────────────────────────────────
 * Emits ONE Sentry breadcrumb (`category: 'imf.state'`) whenever the app's state
 * changes, so the imp validation pipeline can attribute real (staging) error
 * sessions to a modelled state. No PII — payload is a hard-coded state name.
 *
 * Adapted for this repo (PolePosition / IndyWork Dashboard):
 *   • react-router v5 (useLocation from 'react-router-dom') — NOT v6.
 *   • Mounted once inside <BrowserRouter> in src/components/apollo.js.
 *   • Sentry (@sentry/react) is initialized with `environment` in src/index.jsx.
 *
 * Route regexes were reconciled with src/components/routes/route-constants.js.
 * Keep the state strings in sync with the modelled `status` values (snake_case);
 * map generated from the live model on 2026-06-25.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

/** Emit the breadcrumb. Deduped — repeated calls with the same state are skipped. */
let _last: string | null = null;
export function markImpState(stateName: string): void {
  if (!stateName || stateName === _last) return;
  _last = stateName;
  Sentry.addBreadcrumb({
    category: 'imf.state',
    level: 'info',
    message: stateName,
    data: { 'imf.state': stateName },
  });
}

/**
 * Route → modelled status. MOST-SPECIFIC FIRST (first match wins).
 * Verified against route-constants.js + the implication model (2026-06-25).
 *
 * ONLY paths the URL alone can prove belong here. Routes whose state depends on
 * the user's role or on a preceding event (admin_blasts_list, booking_created,
 * club_selected, dancer_logged_in) are intentionally absent — they are emitted
 * only by the role/event-aware helpers below to avoid false breadcrumbs.
 */
const ROUTE_TO_STATE: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/login/, 'web_landing_page'],
  [/^\/admin\/entertainers/, 'admin_entertainers'],
  [/^\/groups\/\d+/, 'group_detail'], // RouteConstants.groupDetails = /groups/:groupId (numeric)
  [/^\/social\/scheduled-posts\/add/, 'iter1_social_scheduled_posts_add'], // keep above the list
  [/^\/social\/scheduled-posts/, 'scheduled_posts_list'],
  [/^\/conversations/, 'iter1_conversations_list'],
  [/^\/settings/, 'iter1_settings_create'],
  [/^\/entertainers/, 'entertainers'],
];

/** Render once inside the Router. Emits imf.state on every route change. */
export default function ImpStateTracker(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    const hit = ROUTE_TO_STATE.find(([re]) => re.test(pathname));
    if (hit) {
      markImpState(hit[1]);
    } else {
      // Unmapped path (e.g. /clubs, /admin/blasts) is still a real transition
      // away from the last mapped state. Reset the dedupe guard so re-entering
      // that same state later emits a fresh breadcrumb instead of being skipped.
      _last = null;
    }
  }, [pathname]);
  return null;
}

/* ── Cases the URL ALONE cannot distinguish — call these on the real event ───────
 *
 * These states are NOT in ROUTE_TO_STATE because the URL cannot prove them.
 * Emit them from the role/event-aware code path that actually knows the context.
 *
 * A) Same URL, different state:
 *      • /clubs is BOTH `logged_in` (just authenticated) AND `club_selected`.
 *        Neither is in ROUTE_TO_STATE — the route effect emits nothing for /clubs —
 *        so emit the correct one explicitly from the code path that knows which:
 *           afterLoginSuccess(() => markLoggedIn());
 *           onClubSelected(()    => markClubSelected());
 *      • /admin/blasts is `admin_blasts_list` for admin, `dancer_logged_in` for dancer.
 *        Emit the right one once the role is known:
 *           user.role === 'dancer' ? markDancerLoggedIn() : markAdminBlastsList();
 *
 * B) /calendar models `booking_created` (state right after a booking is made),
 *    but an ordinary calendar visit is NOT booking_created. Emit only on the event:
 *           onBookingCreated(() => markBookingCreated());
 *
 * C) Modals / wizard steps that never change the URL:
 *           onOpenInviteModal(() => markImpState('group_invite_modal'));
 * ──────────────────────────────────────────────────────────────────────────── */
export const markLoggedIn = (): void => markImpState('logged_in');
export const markClubSelected = (): void => markImpState('club_selected');

// Role/event-aware marks for routes the URL alone cannot prove.
export const markBookingCreated = (): void => markImpState('booking_created');
export const markAdminBlastsList = (): void => markImpState('admin_blasts_list');
export const markDancerLoggedIn = (): void => markImpState('dancer_logged_in');
